import React, { Suspense, lazy } from 'react';
import { Routes, Route, Outlet } from 'react-router-dom';
import { SettingsProvider } from './context/SettingsContext';

// Eagerly loaded components (Layouts & UI)
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import WhatsAppButton from './components/common/WhatsAppButton';
import ScrollToTop from './components/common/ScrollToTop';
import BackToTop from './components/common/BackToTop';
import PopupNotice from './components/common/PopupNotice';
import Loader from './components/common/Loader';
import ProtectedRoute from './components/common/ProtectedRoute';
import SEOHead from './components/common/SEOHead';

// ✅ THE FIX: A modern "Skeleton" loader that reacts instantly (no 200ms lag).
const PageLoader = () => (
  <div className="w-full min-h-screen bg-white flex flex-col">
    {/* Dark placeholder banner for the transparent navbar */}
    <div className="w-full h-[350px] bg-slate-900 flex flex-col items-center justify-center pt-20 relative overflow-hidden">
      {/* Subtle background pulse */}
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 animate-pulse z-0"></div>
      
      {/* Spinner */}
      <div className="w-10 h-10 border-4 border-slate-700 border-t-primary-500 rounded-full animate-spin mb-4 relative z-10"></div>
      <p className="text-slate-400 text-sm font-medium relative z-10 tracking-wide">Loading page...</p>
    </div>
    
    {/* Content skeleton to prevent the Footer from jumping up */}
    <div className="max-w-5xl mx-auto w-full px-4 py-16 space-y-8 animate-pulse opacity-60">
      <div className="h-10 bg-slate-200 rounded-lg w-1/3"></div>
      <div className="space-y-4">
        <div className="h-4 bg-slate-200 rounded w-full"></div>
        <div className="h-4 bg-slate-200 rounded w-5/6"></div>
        <div className="h-4 bg-slate-200 rounded w-4/6"></div>
      </div>
      <div className="space-y-4 pt-8">
        <div className="h-4 bg-slate-200 rounded w-full"></div>
        <div className="h-4 bg-slate-200 rounded w-11/12"></div>
      </div>
    </div>
  </div>
);

// ✅ Shared Public Layout with default SEO fallback
const PublicLayout = () => (
  <>
    {/* SEO: Default meta tags — individual pages override these via react-helmet-async */}
    <SEOHead />
    <Navbar />
    <div className="min-h-screen flex flex-col"> 
      <Suspense fallback={<PageLoader />}>
        <main className="flex-grow" role="main">
          <Outlet /> {/* This is where the pages will load */}
        </main>
      </Suspense>
    </div>
    <Footer />
  </>
);

const AdminPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <Loader fullScreen={false} />
  </div>
);

// === LAZY LOAD PUBLIC PAGES ===
const Home = lazy(() => import('./pages/public/Home'));
const About = lazy(() => import('./pages/public/About'));
const Services = lazy(() => import('./pages/public/Services'));
const StudyAbroad = lazy(() => import('./pages/public/StudyAbroad'));
const Universities = lazy(() => import('./pages/public/Universities'));
const UniversityDetail = lazy(() => import('./pages/public/UniversityDetail'));
const Courses = lazy(() => import('./pages/public/Courses'));
const CourseDetail = lazy(() => import('./pages/public/CourseDetail'));
const TrackApplication = lazy(() => import('./pages/public/TrackApplication'));
const Apply = lazy(() => import('./pages/public/Apply'));
const BookAppointment = lazy(() => import('./pages/public/BookAppointment'));
const Blog = lazy(() => import('./pages/public/Blog'));
const BlogDetail = lazy(() => import('./pages/public/BlogDetail'));
const Contact = lazy(() => import('./pages/public/Contact'));
const Gallery = lazy(() => import('./pages/public/Gallery'));
const Scholarships = lazy(() => import('./pages/public/Scholarships'));
const Events = lazy(() => import('./pages/public/Events'));
const CompareUniversities = lazy(() => import('./pages/public/CompareUniversities'));
const FAQ = lazy(() => import('./pages/public/FAQ'));
const Testimonials = lazy(() => import('./pages/public/Testimonials'));
const Login = lazy(() => import('./pages/public/Login'));
const NotFound = lazy(() => import('./pages/public/NotFound'));
const PrivacyPolicy = lazy(() => import('./pages/public/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/public/Terms'));

// === LAZY LOAD ADMIN PAGES ===
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const ManageApplications = lazy(() => import('./pages/admin/ManageApplications'));
const ManageUniversities = lazy(() => import('./pages/admin/ManageUniversities'));
const ManageCourses = lazy(() => import('./pages/admin/ManageCourses'));
const ManageBlogs = lazy(() => import('./pages/admin/ManageBlogs'));
const ManageTestimonials = lazy(() => import('./pages/admin/ManageTestimonials'));
const ManageAppointments = lazy(() => import('./pages/admin/ManageAppointments'));
const ManageContacts = lazy(() => import('./pages/admin/ManageContacts'));
const ManageStudyAbroad = lazy(() => import('./pages/admin/ManageStudyAbroad'));
const ManageGallery = lazy(() => import('./pages/admin/ManageGallery'));
const ManageScholarships = lazy(() => import('./pages/admin/ManageScholarships'));
const ManageEvents = lazy(() => import('./pages/admin/ManageEvents'));
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'));

// Settings Pages
const GeneralSettings = lazy(() => import('./pages/admin/settings/GeneralSettings'));
const FooterSettings = lazy(() => import('./pages/admin/settings/FooterSettings'));
const SocialMediaSettings = lazy(() => import('./pages/admin/settings/SocialMediaSettings'));
const WhatsAppSettings = lazy(() => import('./pages/admin/settings/WhatsAppSettings'));
const AboutPageSettings = lazy(() => import('./pages/admin/settings/AboutPageSettings'));
const PrivacyPolicySettings = lazy(() => import('./pages/admin/settings/PrivacyPolicySettings'));
const HomePageSettings = lazy(() => import('./pages/admin/settings/HomePageSettings'));
const ServicesPageSettings = lazy(() => import('./pages/admin/settings/ServicesPageSettings'));
const ContactPageSettings = lazy(() => import('./pages/admin/settings/ContactPageSettings'));
const FAQPageSettings = lazy(() => import('./pages/admin/settings/FAQPageSettings'));
const HeaderSettings = lazy(() => import('./pages/admin/settings/HeaderSettings'));
const PopupSettings = lazy(() => import('./pages/admin/settings/PopupSettings'));

function App() {
  return (
    <SettingsProvider>
      <ScrollToTop />
      
      <Routes>
        {/* ==================== PUBLIC ROUTES ==================== */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/universities" element={<Universities />} />
          <Route path="/universities/:slug" element={<UniversityDetail />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/courses/:slug" element={<CourseDetail />} />
          <Route path="/track-application" element={<TrackApplication />} />
          <Route path="/apply" element={<Apply />} />
          <Route path="/book-appointment" element={<BookAppointment />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/scholarships" element={<Scholarships />} />
          <Route path="/events" element={<Events />} />
          <Route path="/compare-universities" element={<CompareUniversities />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/study-in/:slug" element={<StudyAbroad />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="/login" element={
          <Suspense fallback={<AdminPageLoader />}>
            <Login />
          </Suspense>
        } />

        {/* ==================== ADMIN ROUTES ==================== */}
        <Route path="/admin" element={
          <ProtectedRoute>
            <Suspense fallback={<AdminPageLoader />}>
              <AdminLayout />
            </Suspense>
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="gallery" element={<ManageGallery />} />
          <Route path="scholarships" element={<ManageScholarships />} />
          <Route path="events" element={<ManageEvents />} />
          <Route path="profile" element={<AdminProfile />} />
          <Route path="applications" element={<ManageApplications />} />
          <Route path="universities" element={<ManageUniversities />} />
          <Route path="courses" element={<ManageCourses />} />
          <Route path="blogs" element={<ManageBlogs />} />
          <Route path="testimonials" element={<ManageTestimonials />} />
          <Route path="appointments" element={<ManageAppointments />} />
          <Route path="contacts" element={<ManageContacts />} />
          <Route path="study-abroad" element={<ManageStudyAbroad />} />
          
          {/* Settings */}
          <Route path="settings/general" element={<GeneralSettings />} />
          <Route path="settings/home" element={<HomePageSettings />} />
          <Route path="settings/about" element={<AboutPageSettings />} />
          <Route path="settings/services" element={<ServicesPageSettings />} />
          <Route path="settings/contact" element={<ContactPageSettings />} />
          <Route path="settings/footer" element={<FooterSettings />} />
          <Route path="settings/social" element={<SocialMediaSettings />} />
          <Route path="settings/whatsapp" element={<WhatsAppSettings />} />
          <Route path="settings/faq" element={<FAQPageSettings />} />
          <Route path="settings/popup" element={<PopupSettings />} />
          <Route path="settings/header" element={<HeaderSettings />} />
          <Route path="settings/legal" element={<PrivacyPolicySettings />} />
        </Route>
      </Routes>
      
      <WhatsAppButton />
      <BackToTop />
      <PopupNotice />
    </SettingsProvider>
  );
}

export default App;